import type { OpenPositionCustomerAggregate } from "@/common/api/open-position/use-open-position-customer-aggregate-search.query";
import { formatCurrency } from "@/common/helpers/format-currency";
import { formatApiDateOnlyToLocale } from "@/common/helpers/format-date";
import { ImplementedAccessorColumnDef } from "@/components/data-table/types";
import { EmptyValue } from "@/components/ui/empty-value";
import i18next from "i18next";

export type OpenPositionAggregateMetrics = Omit<OpenPositionCustomerAggregate, "customer">;

export function createAggregateMetricColumns<TRow extends OpenPositionAggregateMetrics>() {
  return {
    invoiceBalance: {
      id: "invoiceBalance",
      size: 150,
      enableSorting: true,
      align: "right",
      header: () => i18next.t("fia:InvoiceBalance"),
      accessorFn: (row: TRow) => row.invoiceBalance,
      cell: (info) => formatCurrency(info.getValue()),
    } as ImplementedAccessorColumnDef<TRow, number>,

    paymentBalance: {
      id: "paymentBalance",
      size: 150,
      enableSorting: true,
      align: "right",
      header: () => i18next.t("fia:PaymentBalance"),
      accessorFn: (row: TRow) => row.paymentBalance,
      cell: (info) => formatCurrency(info.getValue()),
    } as ImplementedAccessorColumnDef<TRow, number>,

    totalBalance: {
      id: "totalBalance",
      size: 150,
      enableSorting: true,
      align: "right",
      header: () => i18next.t("fia:TotalBalance"),
      accessorFn: (row: TRow) => row.totalBalance,
      cell: (info) => formatCurrency(info.getValue()),
    } as ImplementedAccessorColumnDef<TRow, number>,

    positionCount: {
      id: "positionCount",
      size: 100,
      enableSorting: true,
      align: "right",
      header: () => i18next.t("fia:PositionCount"),
      accessorFn: (row: TRow) => row.positionCount,
      cell: (info) => info.getValue(),
    } as ImplementedAccessorColumnDef<TRow, number>,

    overdueAmount: {
      id: "overdueAmount",
      size: 170,
      enableSorting: true,
      align: "right",
      header: () => i18next.t("fia:OverdueAmount"),
      accessorFn: (row: TRow) => row,
      cell: (info) => {
        const row = info.getValue();

        if (row.overdueAmount <= 0) return <EmptyValue />;

        return (
          <div className="flex flex-col items-end">
            <span className="text-error">{formatCurrency(row.overdueAmount)}</span>
            {row.oldestDueDate && (
              <span className="text-muted-foreground text-xs">
                {i18next.t("fia:OldestDue", { date: formatApiDateOnlyToLocale(row.oldestDueDate) })}
              </span>
            )}
          </div>
        );
      },
    } as ImplementedAccessorColumnDef<TRow, TRow>,
  };
}
